"use client";

import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import Image from "next/image";
import { useEffect, useState } from "react";

type CreatorProps = {
  type: "creator";
  avatar: string;
  name: string;
  role: string;
  profile?: string;
};

type WorkProps = {
  type: "work";
  thumbnail: string;
  title: string;
  year: string;
  creator?: string;
  category?: string;
  isbn?: string;
};

export type CenterCardProps = CreatorProps | WorkProps;

// openBD APIから書影URLを取得するカスタムフック
function useBookCover(isbn?: string, category?: string): string | null {
  const [coverUrl, setCoverUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!isbn || (category !== "マンガ" && category !== "小説")) return;
    fetch(`https://api.openbd.jp/v1/get?isbn=${isbn}`)
      .then((r) => r.json())
      .then((data) => {
        const url = data?.[0]?.summary?.cover;
        if (url) setCoverUrl(url);
      })
      .catch(() => {});
  }, [isbn, category]);

  return coverUrl;
}

export default function CenterCard(props: CenterCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const coverUrl = useBookCover(
    props.type === "work" ? props.isbn : undefined,
    props.type === "work" ? props.category : undefined,
  );

  return (
    <>
      <div
        className="relative z-10 animate-fade-in-delay cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="relative rounded-2xl border-2 border-[#667eea]/30 bg-gradient-to-br from-[#667eea]/10 to-[#764ba2]/10 px-8 py-10 text-center shadow-[0_20px_60px_rgba(102,126,234,0.2)] backdrop-blur-md transition-all duration-300 hover:scale-105 hover:shadow-[0_30px_80px_rgba(102,126,234,0.4)]">
          {/* 左矢印（デスクトップのみ表示） */}
          <div className="absolute left-[-70px] top-1/2 hidden h-0.5 w-20 -translate-y-1/2 bg-gradient-to-r from-[#667eea] to-transparent lg:block">
            <div className="absolute left-0 top-1/2 size-3 -translate-y-1/2 rotate-[-135deg] border-r-2 border-t-2 border-[#667eea]" />
          </div>

          {/* 右矢印（デスクトップのみ表示） */}
          <div className="absolute right-[-70px] top-1/2 hidden h-0.5 w-20 -translate-y-1/2 bg-gradient-to-l from-transparent to-[#667eea] lg:block">
            <div className="absolute left-0 top-1/2 size-3 -translate-y-1/2 rotate-[-135deg] border-r-2 border-t-2 border-[#667eea]" />
          </div>

          {props.type === "creator" ? (
            <CreatorContent
              avatar={props.avatar}
              name={props.name}
              role={props.role}
            />
          ) : (
            <WorkContent
              thumbnail={props.thumbnail}
              title={props.title}
              year={props.year}
              creator={props.creator}
              coverUrl={coverUrl}
            />
          )}
        </div>
      </div>

      {/* 詳細モーダル */}
      <Dialog
        open={isOpen}
        onClose={setIsOpen}
        className="relative z-10"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 flex items-center justify-center overflow-y-auto p-4">
          <DialogPanel
            transition
            className="relative w-full max-w-sm rounded-2xl border border-white/10 bg-[#1a1a2e] p-6 shadow-2xl transition data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
          >
            {/* クローズボタン */}
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="absolute right-4 top-4 text-white/50 hover:text-white"
            >
              <XMarkIcon className="size-6" />
            </button>

            {/* モーダル内容 */}
            <div className="flex flex-col items-center gap-4">
              {props.type === "creator" ? (
                <CreatorModalContent
                  avatar={props.avatar}
                  name={props.name}
                  role={props.role}
                  profile={props.profile}
                />
              ) : (
                <WorkModalContent
                  thumbnail={props.thumbnail}
                  title={props.title}
                  year={props.year}
                  creator={props.creator}
                  coverUrl={coverUrl}
                />
              )}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
}

/** カード内：作家コンテンツ */
function CreatorContent({ avatar, name, role }: Omit<CreatorProps, "type">) {
  return (
    <>
      <div className="mx-auto mb-5 flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-5xl shadow-[0_10px_30px_rgba(102,126,234,0.5)]">
        {avatar}
      </div>
      <div className="mb-2.5 bg-gradient-to-br from-white to-[#8b92a8] bg-clip-text text-base font-bold text-transparent">
        {name}
      </div>
      <div className="text-sm tracking-widest text-[#667eea]">{role}</div>
    </>
  );
}

/** カード内：作品コンテンツ */
function WorkContent({
  thumbnail,
  title,
  year,
  creator,
  coverUrl,
}: Omit<WorkProps, "type"> & { coverUrl: string | null }) {
  return (
    <>
      <div className="mx-auto mb-5 flex size-16 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#667eea]/30 to-[#764ba2]/30 text-2xl">
        {coverUrl ? (
          <Image
            src={coverUrl}
            alt={title}
            width={64}
            height={64}
            className="h-full w-full object-cover"
            unoptimized
          />
        ) : (
          thumbnail
        )}
      </div>
      <div className="mb-2.5 bg-gradient-to-br from-white to-[#8b92a8] bg-clip-text text-xl font-bold text-transparent">
        {title}
      </div>
      <div className="text-sm text-[#8b92a8]">{year}</div>
      {creator && <div className="mt-2 text-sm text-[#667eea]">{creator}</div>}
    </>
  );
}

/** モーダル内：作家コンテンツ */
function CreatorModalContent({
  avatar,
  name,
  role,
  profile,
}: Omit<CreatorProps, "type">) {
  return (
    <>
      <div className="flex size-24 items-center justify-center rounded-full bg-gradient-to-br from-[#667eea] to-[#764ba2] text-4xl shadow-[0_10px_30px_rgba(102,126,234,0.5)]">
        {avatar}
      </div>
      <h3 className="text-center text-lg font-bold text-white">{name}</h3>
      <p className="text-sm tracking-widest text-[#667eea]">{role}</p>
      {profile && (
        <div className="mt-4 w-full">
          <p className="mb-1 text-center text-[10px] font-semibold uppercase tracking-widest text-[#667eea]">profile</p>
          <p className="text-center text-sm leading-relaxed text-[#c4c9d8]">{profile}</p>
        </div>
      )}
      {/* <button
        type="button"
        className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
      >
        この作家に影響を受けた作家を見る
      </button> */}
    </>
  );
}

/** モーダル内：作品コンテンツ */
function WorkModalContent({
  thumbnail,
  title,
  year,
  creator,
  coverUrl,
}: Omit<WorkProps, "type"> & { coverUrl: string | null }) {
  return (
    <>
      <div className="flex size-20 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br from-[#667eea]/30 to-[#764ba2]/30 text-3xl">
        {coverUrl ? (
          <Image
            src={coverUrl}
            alt={title}
            width={80}
            height={80}
            className="h-full w-full object-cover"
            unoptimized
          />
        ) : (
          thumbnail
        )}
      </div>
      <h3 className="text-center text-lg font-bold text-white">{title}</h3>
      <p className="text-sm text-[#8b92a8]">{year}</p>
      {creator && <p className="text-sm text-[#667eea]">{creator}</p>}
      {/* <button
        type="button"
        className="mt-4 w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white"
      >
        この作品に影響を受けた作品を見る
      </button> */}
    </>
  );
}
